import React, { useMemo, useState } from 'react';
import { FaCheckCircle, FaFilter, FaSort, FaSortDown, FaSortUp, FaTimesCircle, FaTrash } from 'react-icons/fa';
import '../styles/Table.css';
import CommonButton from './CommonButton';
import { FaPencil } from 'react-icons/fa6';

const Table = ({ columns, data, onApprove, onReject, onEdit, onDelete, actions, onFiltersChange }) => {
  const [processing, setProcessing] = useState({ id: null, action: null });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filters, setFilters] = useState({});
  const [openFilterKey, setOpenFilterKey] = useState(null);

  const handleAction = async (fn, id, action) => {
    if (processing.id) return;
    setProcessing({ id, action });
    await fn(id);
    setProcessing({ id: null, action: null });
  };

  const getColumnValue = (row, column) => {
    const value = column.filterValue
      ? column.filterValue(row)
      : row[column.key];

    return value === null || value === undefined ? '' : String(value);
  };

  const requestSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const visibleData = useMemo(() => {
    const filteredData = data.filter((row) =>
      columns.every((column) => {
        const filterValue = filters[column.key];
        if (!filterValue) return true;
        if (column.filterMatch) return column.filterMatch(row, filterValue);
        return getColumnValue(row, column)
          .toLowerCase()
          .includes(filterValue.toLowerCase());
      })
    );

    if (!sortConfig.key) return filteredData;

    const sortedColumn = columns.find((column) => column.key === sortConfig.key);
    if (!sortedColumn) return filteredData;

    return [...filteredData].sort((first, second) => {
      const firstValue = getColumnValue(first, sortedColumn);
      const secondValue = getColumnValue(second, sortedColumn);
      const numericFirst = Number(firstValue);
      const numericSecond = Number(secondValue);
      const areNumbers = firstValue !== '' && secondValue !== '' &&
        !Number.isNaN(numericFirst) && !Number.isNaN(numericSecond);

      const comparison = areNumbers
        ? numericFirst - numericSecond
        : firstValue.localeCompare(secondValue, undefined, { numeric: true, sensitivity: 'base' });

      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [columns, data, filters, sortConfig]);

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort />;
    return sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />;
  };

  return (
    <div className="table-wrapper">
      <table className="custom-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className="sortable-table-header"
                onClick={() => requestSort(col.key)}
              >
                <div className="table-header-content">
                  <span>{col.label}</span>
                  <button
                    type="button"
                    className="table-header-sort"
                    aria-label={`Urutkan ${col.label}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      requestSort(col.key);
                    }}
                  >
                    {getSortIcon(col.key)}
                  </button>
                  <button
                    type="button"
                    className={`table-header-filter ${filters[col.key] ? 'active' : ''}`}
                    aria-label={`Filter ${col.label}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      setOpenFilterKey((current) => current === col.key ? null : col.key);
                    }}
                  >
                    <FaFilter />
                  </button>
                  {openFilterKey === col.key && (
                    <div
                      className="table-filter-menu"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {col.filterOptions ? (
                        <select
                          aria-label={`Filter ${col.label}`}
                          value={filters[col.key] || ''}
                          onChange={(event) => {
                            const value = event.target.value;
                            const next = { ...filters, [col.key]: value };
                            setFilters(next);
                            onFiltersChange?.(next);
                          }}
                        >
                          <option value="">All</option>
                          {col.filterOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="search"
                          list={`table-filter-options-${col.key}`}
                          aria-label={`Cari ${col.label}`}
                          placeholder={`Cari ${col.label}`}
                          value={filters[col.key] || ''}
                          onChange={(event) => {
                            const value = event.target.value;
                            const next = { ...filters, [col.key]: value };
                            setFilters(next);
                            onFiltersChange?.(next);
                          }}
                        />
                      )}
                      {!col.filterOptions && (
                        <datalist id={`table-filter-options-${col.key}`}>
                          {[...new Set(data.map((row) => getColumnValue(row, col)))].map((value) => (
                            <option key={value} value={value}>
                              {value || '-'}
                            </option>
                          ))}
                        </datalist>
                      )}
                      <button
                        type="button"
                        className="table-filter-all"
                        onClick={() => {
                          const next = { ...filters, [col.key]: '' };
                          setFilters(next);
                          onFiltersChange?.(next);
                          setOpenFilterKey(null);
                        }}
                      >
                        All
                      </button>
                    </div>
                  )}
                </div>
              </th>
            ))}
            {(actions || onApprove || onReject || onEdit || onDelete) && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {visibleData.length === 0 ? (
            <tr>
              <td colSpan={columns.length + 1} className="no-data">
                No data available
              </td>
            </tr>
          ) : (
            visibleData.map((row) => (
              <tr 
                key={row.id} 
                className={row.highlight ? "checked-row" : ""}
              >
                {columns.map((col) => (
                  <td key={col.key}>
                    {col.format
                      ? col.format(row[col.key], row)
                      : row[col.key] || '-'}
                  </td>
                ))}
                {(actions || onApprove || onReject || onEdit || onDelete) && (
                  <td className="table-actions">
                    <div className='table-button-wrapper'>
                      {actions && actions(row)}
                      {onApprove && (
                        <CommonButton 
                          className='table-button'
                          loading={processing.id === row.id && processing.action === 'approve'}
                          disabled={processing.id === row.id && processing.action === 'approve'}
                          onClick={() => handleAction(onApprove, row.id, 'approve')}
                        >
                          <FaCheckCircle />
                        </CommonButton>
                      )}
                      {onReject && (
                        <CommonButton 
                          className='table-button'
                          loading={processing.id === row.id && processing.action === 'reject'}
                          disabled={processing.id === row.id && processing.action === 'reject'}
                          onClick={() => handleAction(onReject, row.id, 'reject')}
                        >
                          <FaTimesCircle />
                        </CommonButton>
                      )}
                      {onEdit && (
                        <CommonButton
                          className='table-button' 
                          loading={processing.id === row.id && processing.action === 'edit'}
                          disabled={processing.id === row.id && processing.action === 'edit'}
                          onClick={() => handleAction(onEdit, row.id, 'edit')}
                        >
                          <FaPencil />
                        </CommonButton>
                      )}
                      {onDelete && (
                        <CommonButton
                          className='table-button'
                          loading={processing.id === row.id && processing.action === 'delete'}
                          disabled={processing.id === row.id && processing.action === 'delete'}
                          onClick={() => handleAction(onDelete, row.id, 'delete')}
                        >
                          <FaTrash />
                        </CommonButton>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default Table;
